import React from 'react';
import { Box, Container, Flex, Heading, Image, Text } from '@chakra-ui/react';
import { generateS3DownloadUrl } from '../server/libs/siteSettings';
import FullWidthBox from './FullWidthBox';

// 構成定義（JSON）ファイルに設定するHeroAreaに対する設定値の型
export type HeroAreaSettings = {
  bgColor?: string | null; // 背景画像がない時に表示される背景色
  color?: string | null; // タイトル・サブタイトルをテキストで表示するときの文字色
  title: {
    pc: string;
    mobile?: string | null; // モバイル表示の時に改行が必要な場合は\nで改行
  };
  subTitle?: string | null;
  mainImage?: {
    pc: {
      src?: string; // 画像パス（下位互換用)
      imgSrc?: string; // 画像パス（srcS3Keyよりも優先される）
      imgS3Key?: string; // 画像S3キー
      maxWidth?: number | null;
      maxHeight?: number | null;
    };
    mobile: {
      src?: string; // 画像パス（下位互換用)
      imgSrc?: string; // 画像パス（srcS3Keyよりも優先される）
      imgS3Key?: string; // 画像S3キー
      maxWidth?: number | null;
      maxHeight?: number | null;
    };
  } | null;
  bgImage?: {
    pc?: string | null; // PC表示用背景画像パス（pcS3Keyよりも優先される）
    pcS3Key?: string | null; // PC表示用背景画像S3キー
    mobile?: string | null; // モバイル表示用背景画像パス（mobileS3Keyよりも優先される）
    mobileS3Key?: string | null; // モバイル表示用背景画像S3キー
  } | null;
  noPadding?: boolean; // 画像のみでレイアウトしたい時などでpaddingを無しにする
};

// HeroAreaコンポーネントのpropsの型定義
export type HeroAreaProps = HeroAreaSettings & {
  isMobile: boolean;
  subContent?: React.ReactNode;
};

/**
 * 構成定義の指定要素から画像取得元URLを取得する
 *
 * @param element 画像パス／S3キーのプロパティを含むオブジェクト
 * @returns 画像ファイル取得URL
 */
const imgSrcUrl = (element?: {
  src?: string /* 下位互換用 */;
  imgSrc?: string;
  imgS3Key?: string;
}): string => {
  if (!element) return '';
  return (
    element.src || element.imgSrc || generateS3DownloadUrl(element.imgS3Key)
  );
};

export const HeroArea = ({
  title,
  subTitle,
  color,
  bgColor,
  mainImage,
  bgImage,
  isMobile,
  subContent,
  noPadding,
}: HeroAreaProps) => {
  const textTitle = isMobile && title?.mobile ? title.mobile : title.pc;
  const hasMainImage = !!mainImage?.pc;
  const mobileSrcUrl = imgSrcUrl(mainImage?.mobile);
  const pcSrcUrl = imgSrcUrl(mainImage?.pc);
  const mobileBgSrcUrl =
    bgImage?.mobile ?? generateS3DownloadUrl(bgImage?.mobileS3Key);
  const pcBgSrcUrl = bgImage?.pc ?? generateS3DownloadUrl(bgImage?.pcS3Key);
  return (
    <FullWidthBox
      bg={bgColor ?? undefined}
      backgroundImage={
        isMobile && mobileBgSrcUrl ? mobileBgSrcUrl : pcBgSrcUrl ?? undefined
      }
      backgroundSize="cover"
    >
      <Container>
        <Flex
          minH={230}
          alignItems="center"
          gap={2}
          px={noPadding ? 0 : isMobile ? 0 : 4}
          py={noPadding ? 0 : isMobile ? 6 : 1}
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <Heading
            as="h2"
            fontSize={{ base: 44, md: 48 }}
            textAlign="center"
            color={color ?? undefined}
            whiteSpace="pre-wrap"
            py={noPadding ? 0 : isMobile ? 2 : 4}
            lineHeight="1.5"
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            {hasMainImage ? (
              <Image
                alt={textTitle}
                src={isMobile && mobileSrcUrl ? mobileSrcUrl : pcSrcUrl}
                maxWidth={
                  isMobile && mainImage?.mobile?.maxWidth
                    ? mainImage.mobile.maxWidth ?? undefined
                    : mainImage.pc.maxWidth ?? undefined
                }
                maxHeight={
                  isMobile && mainImage?.mobile?.maxHeight
                    ? mainImage.mobile.maxHeight ?? undefined
                    : mainImage.pc.maxHeight ?? undefined
                }
              />
            ) : (
              textTitle
            )}
            {subTitle && <Text fontSize="2xl">{subTitle}</Text>}
          </Heading>
          {subContent && (
            <Box pb={noPadding && isMobile ? 6 : 0}>{subContent}</Box>
          )}
        </Flex>
      </Container>
    </FullWidthBox>
  );
};
